import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Copy, Send } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Button, Callout, Checkbox, HtmlCodeEditor } from '../../components/ui';
import {
  SeriesStep,
  SeriesVariable,
  useCreateSeriesStep,
  useSendSeriesTest,
  useSeriesPreview,
  useUpdateSeriesStep,
} from '../../queries/emailSeries';

interface EmailSeriesStepDialogProps {
  seriesId: number;
  step: SeriesStep | null; // null = add
  variables: SeriesVariable[];
  canWrite: boolean;
  onClose: () => void;
}

const INPUT = 'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm disabled:opacity-60';

export function EmailSeriesStepDialog({ seriesId, step, variables, canWrite, onClose }: EmailSeriesStepDialogProps) {
  const [name, setName] = useState(step?.name ?? '');
  const [sendAfterDays, setSendAfterDays] = useState(String(step?.sendAfterDays ?? 0));
  const [subject, setSubject] = useState(step?.subject ?? '');
  const [preheader, setPreheader] = useState(step?.preheader ?? '');
  const [html, setHtml] = useState(step?.htmlBody ?? '');
  const [text, setText] = useState(step?.textBody ?? '');
  const [isActive, setIsActive] = useState(step?.isActive ?? true);
  // Opens on the rendered email when there is one, like the campaign dialog.
  const [showPreview, setShowPreview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const create = useCreateSeriesStep();
  const update = useUpdateSeriesStep();
  const sendTest = useSendSeriesTest();
  const content = { subject, preheader, htmlBody: html, textBody: text };
  const previewing = showPreview && html.trim().length > 0;
  const { data: preview, error: previewError } = useSeriesPreview(seriesId, content, previewing);
  const unknown = previewing ? preview?.unknownVariables ?? [] : [];

  const days = Number(sendAfterDays);
  const daysValid = sendAfterDays.trim() !== '' && Number.isInteger(days) && days >= 0 && days <= 365;
  const canSave = canWrite && name.trim() && subject.trim() && html.trim() && daysValid;
  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    setError(null);
    const draft = { name: name.trim(), sendAfterDays: days, subject, preheader, htmlBody: html, textBody: text, isActive };
    const callbacks = { onSuccess: onClose, onError: (err: unknown) => setError(extractErrorMessage(err, 'Save failed.')) };
    if (step) update.mutate({ ...draft, seriesId, stepId: step.id }, callbacks);
    else create.mutate({ ...draft, seriesId }, callbacks);
  };

  const handleTest = () => {
    sendTest.reset();
    sendTest.mutate({ seriesId, ...content });
  };

  const copyVariable = (variable: string) => {
    const token = `{{${variable}}}`;
    void navigator.clipboard?.writeText(token).then(() => setCopied(variable), () => setCopied(null));
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{step ? (canWrite ? 'Edit step' : 'Step') : 'Add step'}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            The email this series sends a set number of days after the trigger.
          </Dialog.Description>

          <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_15rem]">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
                <div>
                  <label htmlFor="step-name" className="mb-1 block text-sm font-medium">Name</label>
                  <input id="step-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canWrite} className={INPUT} />
                </div>
                <div>
                  <label htmlFor="step-days" className="mb-1 block text-sm font-medium">Send after (days)</label>
                  <input id="step-days" type="number" min={0} max={365} value={sendAfterDays} onChange={(e) => setSendAfterDays(e.target.value)} disabled={!canWrite} className={INPUT} />
                </div>
              </div>
              <div>
                <label htmlFor="step-subject" className="mb-1 block text-sm font-medium">Subject</label>
                <input id="step-subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!canWrite} className={INPUT} />
              </div>
              <div>
                <label htmlFor="step-preheader" className="mb-1 block text-sm font-medium">Preheader (optional)</label>
                <input id="step-preheader" value={preheader} onChange={(e) => setPreheader(e.target.value)} disabled={!canWrite} className={INPUT} />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  {previewing ? (
                    <span className="block text-sm font-medium">Email</span>
                  ) : (
                    <label htmlFor="step-html" className="block text-sm font-medium">HTML body</label>
                  )}
                  {html.trim() && (
                    <button type="button" onClick={() => setShowPreview((v) => !v)} className="text-xs font-medium text-primary hover:underline">
                      {showPreview ? 'Edit HTML' : 'Preview email'}
                    </button>
                  )}
                </div>
                {previewing ? (
                  // Same pattern as MarketingCampaignFormDialog: the raw body
                  // shows at once, then the branded sample replaces it (the key
                  // remounts the iframe, as Chrome won't re-navigate srcdoc).
                  // allow-same-origin with no allow-scripts: nothing can run.
                  <iframe
                    key={preview?.html ? 'branded' : 'raw'}
                    title="Email preview"
                    srcDoc={preview?.html ?? html}
                    sandbox="allow-same-origin"
                    className="h-96 w-full rounded-md border border-border bg-white"
                  />
                ) : (
                  <HtmlCodeEditor id="step-html" value={html} onChange={setHtml} readOnly={!canWrite} height="20rem" />
                )}
                {previewing && <p className="mt-1 text-xs text-muted-foreground">Shown with a sample name and code.</p>}
                {previewError && <p className="mt-1 text-xs text-destructive">Couldn't render the preview: {extractErrorMessage(previewError)}</p>}
              </div>
              {unknown.length > 0 && (
                <Callout variant="warning" title="Unknown variables">
                  {unknown.map((v) => `{{${v}}}`).join(', ')} won't be filled in. Check the spelling against the list of variables.
                </Callout>
              )}
              <div>
                <label htmlFor="step-text" className="mb-1 block text-sm font-medium">Plain-text body (optional, made from the HTML if blank)</label>
                <textarea id="step-text" value={text} onChange={(e) => setText(e.target.value)} disabled={!canWrite} rows={4} className={INPUT} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={!canWrite} />
                Step is on
              </label>
            </div>

            <aside className="space-y-2 rounded-md border border-border p-3" aria-label="Variables">
              <p className="text-sm font-medium">Variables</p>
              <p className="text-xs text-muted-foreground">Click one to copy it, then paste it into the subject or body.</p>
              <ul className="space-y-2">
                {variables.map((v) => (
                  <li key={v.name}>
                    <button
                      type="button"
                      onClick={() => copyVariable(v.name)}
                      className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs hover:bg-accent"
                    >
                      {`{{${v.name}}}`}
                      <Copy className="h-3 w-3" />
                    </button>
                    {copied === v.name && <span className="ml-1 text-xs text-muted-foreground">Copied</span>}
                    <p className="mt-0.5 text-xs text-muted-foreground">{v.description}</p>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">The brand header and footer are added automatically.</p>
            </aside>
          </div>

          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {canWrite && (
                <Button variant="outline" size="sm" onClick={handleTest} disabled={sendTest.isPending || !subject.trim() || !html.trim()}>
                  <Send />
                  Send me a test
                </Button>
              )}
              {sendTest.data && <span className="text-sm text-muted-foreground">Test sent to {sendTest.data.sentTo}.</span>}
              {sendTest.error && <span className="text-sm text-destructive">{extractErrorMessage(sendTest.error, 'Test send failed.')}</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>{canWrite ? 'Cancel' : 'Close'}</Button>
              {canWrite && (
                <Button size="sm" onClick={handleSave} disabled={!canSave || saving}>
                  {step ? 'Save step' : 'Add step'}
                </Button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
